/**
 * NotificationRow — single notification with full interactivity.
 * - Tap → navigates to target (post comments, profile, etc.)
 * - Follow request → accept/reject inline buttons
 * - Actor name/avatar → tappable to profile
 * - Comment body shown as preview
 */

import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { uz } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import {
  UserPlus, MessageSquare, Heart, Trophy, Crown, Bell,
  AlertTriangle, Clock, Check, X, Repeat2,
} from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useMarkNotificationRead, useInvalidateInbox } from '@turnifly/shared/hooks'
import { acceptFollowRequest, rejectFollowRequest } from '@turnifly/shared/api'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import type { NotificationItem } from '@turnifly/shared/api'

interface NotificationRowProps {
  notification: NotificationItem
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  follow_request: UserPlus,
  new_follower: UserPlus,
  follow_accepted: UserPlus,
  comment: MessageSquare,
  reply: MessageSquare,
  like: Heart,
  comment_like: Heart,
  repost: Repeat2,
  achievement: Trophy,
  subscription_expiry: Crown,
  habit_penalty: AlertTriangle,
  habit_reminder: Clock,
}

const ICON_COLORS: Record<string, string> = {
  follow_request: 'text-primary',
  new_follower: 'text-primary',
  follow_accepted: 'text-emerald-400',
  comment: 'text-blue-400',
  reply: 'text-blue-400',
  like: 'text-red-400',
  comment_like: 'text-red-400',
  repost: 'text-emerald-400',
  achievement: 'text-amber-400',
}

export const NotificationRow: React.FC<NotificationRowProps> = React.memo(({
  notification,
}) => {
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const markRead = useMarkNotificationRead()
  const invalidateInbox = useInvalidateInbox()
  const Icon = TYPE_ICONS[notification.type] ?? Bell
  const iconColor = ICON_COLORS[notification.type] ?? 'text-text-hint'
  const isUnread = !notification.is_read
  // Show accept/reject only if: follow_request + not yet read (read = already handled)
  const isFollowRequest = notification.type === 'follow_request'
  const showRequestButtons = isFollowRequest && !notification.is_read

  const [requestHandled, setRequestHandled] = useState(false)
  const [requestAction, setRequestAction] = useState<'accepted' | 'rejected' | null>(null)

  const firstActor = Array.isArray(notification.actors) ? notification.actors[0] as Record<string, unknown> | undefined : undefined
  const actorId = firstActor?.id as string | undefined
  const actorName = firstActor?.name as string | undefined

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: false,
    locale: i18n.language === 'uz' ? uz : undefined,
  })

  // Navigate to target on tap
  const handleTap = useCallback((): void => {
    if (isUnread) markRead.mutate(notification.id)

    const { target_type, target_id } = notification
    if (!target_type || !target_id) return

    if (target_type === 'post') {
      navigate(`/feed?post=${target_id}`)
    } else if (target_type === 'profile') {
      navigate(`/profile/${target_id}`)
    }
  }, [notification, isUnread, markRead, navigate])

  // Navigate to actor profile
  const handleActorTap = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    if (actorId) navigate(`/profile/${actorId}`)
  }, [actorId, navigate])

  // Accept follow request
  const handleAccept = useCallback(async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation()
    if (!user?.id || !actorId) return
    hapticImpact('light')
    try {
      await acceptFollowRequest(actorId, user.id)
      setRequestHandled(true)
      setRequestAction('accepted')
      if (isUnread) markRead.mutate(notification.id)
      invalidateInbox()
    } catch { /* silently fail */ }
  }, [user?.id, actorId, isUnread, markRead, notification.id, invalidateInbox])

  // Reject follow request
  const handleReject = useCallback(async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation()
    if (!user?.id || !actorId) return
    hapticImpact('light')
    try {
      await rejectFollowRequest(actorId, user.id)
      setRequestHandled(true)
      setRequestAction('rejected')
      if (isUnread) markRead.mutate(notification.id)
      invalidateInbox()
    } catch { /* silently fail */ }
  }, [user?.id, actorId, isUnread, markRead, notification.id, invalidateInbox])

  return (
    <button
      onClick={handleTap}
      className={`
        flex items-start gap-3 w-full px-4 py-3.5 text-left transition-colors tap-highlight-transparent
        ${isUnread ? 'bg-primary/[0.03]' : 'hover:bg-white/[0.02]'}
      `}
    >
      {/* Avatar — tappable to profile */}
      <div className="relative shrink-0" onClick={handleActorTap}>
        {firstActor && (firstActor.avatar_url as string) ? (
          <img
            src={firstActor.avatar_url as string}
            alt=""
            className="w-11 h-11 rounded-full object-cover ring-1 ring-white/[0.06]"
            loading="lazy"
          />
        ) : (
          <div className="w-11 h-11 rounded-full bg-white/[0.06] flex items-center justify-center">
            <Icon size={18} className={iconColor} />
          </div>
        )}
        {isUnread && (
          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary ring-2 ring-[var(--color-bg)]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title with tappable actor name */}
        <p className={`text-[13px] leading-snug ${isUnread ? 'text-text font-medium' : 'text-text/70'}`}>
          {actorName && (
            <span
              onClick={handleActorTap}
              className="font-semibold hover:underline"
            >
              {actorName}
            </span>
          )}
          {actorName ? ' ' : ''}
          {notification.title.replace(actorName ?? '', '').trim()}
        </p>

        {/* Comment preview */}
        {notification.body && (
          <p className="text-[12px] text-text-hint/60 mt-0.5 line-clamp-2 leading-relaxed">
            {notification.body}
          </p>
        )}

        {/* Follow request actions */}
        {showRequestButtons && !requestHandled && (
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleAccept}
              className="flex items-center gap-1 px-3.5 py-1.5 rounded-lg bg-primary text-white text-[12px] font-semibold tap-highlight-transparent hover:bg-primary/90 transition-colors"
            >
              <Check size={13} />
              {t('inbox.accept')}
            </button>
            <button
              onClick={handleReject}
              className="flex items-center gap-1 px-3.5 py-1.5 rounded-lg bg-white/[0.06] text-text-hint text-[12px] font-medium tap-highlight-transparent hover:bg-white/[0.1] transition-colors"
            >
              <X size={13} />
              {t('inbox.reject')}
            </button>
          </div>
        )}

        {/* Request handled feedback */}
        {(showRequestButtons || isFollowRequest) && requestHandled && (
          <p className={`text-[12px] mt-1.5 font-medium ${
            requestAction === 'accepted' ? 'text-emerald-400' : 'text-text-hint/40'
          }`}>
            {requestAction === 'accepted' ? t('inbox.requestAccepted') : t('inbox.requestRejected')}
          </p>
        )}

        <span className="text-[11px] text-text-hint/40 mt-1 block">{timeAgo}</span>
      </div>
    </button>
  )
})
NotificationRow.displayName = 'NotificationRow'
